import { useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { MdImage } from 'react-icons/md';
import type { TUploadedFile } from '@onesource/schemas';
import { MediaPreviewDialog, MediaThumbnail } from '@onesource/components';

type TMediaGridProps = {
  files: TUploadedFile[];
  isLoading: boolean;
};

export function MediaGrid({ files, isLoading }: TMediaGridProps) {
  const theme = useTheme();
  const [previewFile, setPreviewFile] = useState<TUploadedFile | null>(null);

  const handlePreview = useCallback((file: TUploadedFile) => setPreviewFile(file), []);
  const handleClosePreview = useCallback(() => setPreviewFile(null), []);
  const grid = useMemo(
    () =>
      files.map(file => (
        <Grid item key={file.key} xs={6} sm={4} md={3} lg={2}>
          <MediaThumbnail file={file} isSelected={false} onSelect={handlePreview} onPreview={handlePreview} />
        </Grid>
      )),
    [files, handlePreview],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (files.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 10,
          gap: 1.5,
          color: 'text.secondary',
        }}
      >
        <MdImage size={56} color={theme.palette.action.disabled} />
        <Typography variant="body2">No files uploaded yet. Click "Upload" to add files.</Typography>
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={1.5}>
        {grid}
      </Grid>
      <MediaPreviewDialog file={previewFile} onClose={handleClosePreview} />
    </>
  );
}
